'use client';

import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

const TeamHeroSection = () => {
  return (
    <section className="relative bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 pt-24 pb-16 overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 right-10 w-72 h-72 bg-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-secondary rounded-full blur-3xl"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-4xl mx-auto">
          {/* Hero Image - Nature to Technology Convergence */}
          <div className="mb-12 rounded-2xl overflow-hidden shadow-natural-xl animate-fade-in">
            <AppImage
              src="/assets/images/Screenshot_2026-01-06_123552-1767800286071.png"
              alt="Nature-technology convergence showing seamless transition from natural landscapes with wildlife to quantum computing circuits, representing GenCarbon ESG's technology-driven sustainability approach"
              width={1200}
              height={600}
              className="w-full h-auto object-cover"
              priority
            />
          </div>

          {/* Badge */}
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 text-primary rounded-full mb-6 animate-fade-in">
            <Icon name="UsersIcon" size={20} />
            <span className="text-sm font-semibold">Leadership Excellence</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-text-primary mb-6 animate-fade-in">
            Our GenCarbon Team
          </h1>

          {/* About the Founder Section */}
          <div className="text-left bg-card border border-border rounded-2xl p-8 mb-8 shadow-natural-lg animate-fade-in">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-primary mb-6">
              About the Founder – GenCarbon ESG
            </h2>
            
            <div className="space-y-4 text-base sm:text-lg text-text-secondary leading-relaxed">
              <p>
                The Gencarbon ESG Team has delivered enterprise at scale, spatial analytics, and sustainability, 
                building solutions that move ESG from theory to last-mile, financially viable impact.
              </p>
              
              <p>
                At GenCarbon, sustainability is not approached as a checklist or reporting exercise. Instead, it is 
                treated as a systems problem—one that requires deep understanding of industry domains, local communities, 
                natural capital, and economic incentives. Using an AI-driven framework that integrates spatial intelligence, 
                drone-based data capture, quantum-inspired optimisation, and financial modelling, GenCarbon designs ESG 
                solutions that are turnkey, scalable, and investable.
              </p>
              
              <p>
                The Founders Team has led and contributed to ESG and sustainability initiatives across Europe, the USA, 
                Southeast Asia, and global emerging markets, spanning oil & energy transition, banking and green finance, 
                land and water restoration, and industrial decarbonisation. This includes work in Finland (land restoration 
                and nature-based solutions), the Rhine Valley in Germany (river basin and industrial sustainability), and 
                Southeast Asia (green finance and sustainable capital deployment), solar deployment and efficiency projects 
                on water resources at 25-50MV.
              </p>
              
              <p>
                GenCarbon ESG is not a typical sustainability firm. It is an aggregation and convergence platform—bringing 
                together innovation, finance, technology, and ecology. The work spans solar on water bodies, water conservation 
                and reduction of water-intensity in industry, low-emission pathways, and regenerative agriculture.
              </p>
              
              <p>
                One of the flagship initiatives includes a nationwide India study on non-traditional biochar, covering seven 
                alternative feedstocks, with a projected ₹1,320 crore annual revenue pipeline, focused on farmland rejuvenation, 
                mining slag reuse, and carbon removal economics. Additional initiatives include basalt rock erosion for soil 
                regeneration, blue flagging and blue credit frameworks, and AI-led optimisation of carbon credit generation 
                and monetisation.
              </p>
              
              <p>
                The GenCarbon approach deliberately connects AI + quantum-inspired thinking + sustainability + carbon markets 
                to solve real problems—ensuring that environmental impact is matched with profitability, regulatory readiness, 
                and long-term economic value. The firm is now also extending its problem-solving lens to emerging frontiers 
                such as space sustainability and space debris (space junk) management, recognising that sustainability challenges 
                do not stop at Earth's surface.
              </p>
              
              <p className="italic text-text-primary font-medium">
                At its core, GenCarbon ESG believes that systemic change is a long journey—and that meaningful impact requires 
                collaboration. The firm works both independently and with global collaborators, guided by a simple philosophy:
              </p>
              
              <p className="text-xl font-heading font-bold text-primary text-center mt-4">
                If the journey is long, we need friends.
              </p>
            </div>
          </div>

          {/* Description */}
          <p className="text-lg sm:text-xl text-text-secondary mb-8 leading-relaxed animate-fade-in">
            Led by industry pioneers with decades of combined expertise in ESG compliance, 
            carbon accounting, AI-powered sustainability solutions, and geospatial technology. 
            Our leadership drives innovation in environmental sustainability across global markets.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
            {[
              { icon: 'AcademicCapIcon', value: '30+', label: 'Years Combined Experience' },
              { icon: 'GlobeAltIcon', value: '500+', label: 'Organizations Served' },
              { icon: 'SparklesIcon', value: '50+', label: 'Certifications & Awards' },
              { icon: 'ChartBarIcon', value: '100M+', label: 'Tonnes CO₂ Tracked' },
            ]?.map((stat, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-xl p-6 hover:shadow-natural-lg transition-all duration-300 ease-organic animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex justify-center mb-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name={stat?.icon} size={24} className="text-primary" />
                  </div>
                </div>
                <div className="text-2xl font-heading font-bold text-primary mb-1">
                  {stat?.value}
                </div>
                <div className="text-xs text-text-secondary">{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamHeroSection;